import * as React from 'react';

/**
 * True for anything rendered inside a floating overlay (Dialog, Sheet, Drawer) rather than
 * on the page itself.
 *
 * Overlays sit at `z-50`/`z-[51]`, ABOVE the app's floating OptiBot chat button (`z-40`), so
 * the button paints behind the overlay scrim and can never cover overlay content. Anything
 * that reserves space for that button therefore has to stop doing so inside an overlay.
 *
 * Why a context and not a prop: components get reused in both places — e.g.
 * `OpsScheduleListSidebar` renders on a page AND inside a dialog. Ambient detection means
 * neither the overlay nor the page has to remember to pass anything, which is the point:
 * the behaviour can't be forgotten.
 *
 * Consumer: `apps/web/src/components/shared/ScrollContainer.tsx`.
 */
const InsideOverlayContext = React.createContext(false);

export function InsideOverlayProvider({ children }: { children: React.ReactNode }) {
  return <InsideOverlayContext.Provider value={true}>{children}</InsideOverlayContext.Provider>;
}

/** `true` when the calling component is rendered inside a Dialog / Sheet / Drawer. */
export function useInsideOverlay() {
  return React.useContext(InsideOverlayContext);
}
